/**
 * Páginas de herramienta por tipo de documento («X a Excel»).
 *
 * Fase 2 del plan internacional (SEO.md). Cada página preselecciona la plantilla
 * del widget y enseña la salida REAL del documento de muestra de esa plantilla
 * (public/samples/), consultada en scanner_results el 16-9-2026. Si cambia una
 * muestra, hay que actualizar aquí su `sample`.
 *
 * Los slugs van en inglés en los cuatro idiomas, igual que el resto del sitio
 * (/es/id-scanner, /es/blog/...): el hreflang empareja la misma ruta.
 */
export const DOC_TOOLS = {
  "invoice-to-excel": {
    ns: "invoiceTool",
    template: "invoice",
    // Quien tiene muchas facturas: página de facturas de proveedores en bloque (solo donde existe, toolLangs.json).
    bulkPage: "supplier-invoices-to-excel",
    sample: {
      file: "factura-ejemplo.pdf",
      sheet: "invoices.xlsx",
      columns: ["invoice_number", "issue_date", "supplier_name", "supplier_tax_id", "tax_amount", "total_amount"],
      rows: [
        { invoice_number: "F-2026-0412", issue_date: "2026-08-12", supplier_name: "Suministros Marbella S.L.", supplier_tax_id: "B-11223344", tax_amount: 145.28, total_amount: 837.08 },
      ],
      money: ["tax_amount", "total_amount"],
    },
  },
  "receipt-to-excel": {
    ns: "receiptTool",
    template: "receipt",
    sample: {
      file: "ticket-ejemplo.pdf",
      sheet: "expenses.xlsx",
      columns: ["merchant_name", "date", "payment_method", "tax_amount", "total_amount"],
      rows: [
        { merchant_name: "Cafetería El Mirador", date: "2026-08-22", payment_method: "Tarjeta", tax_amount: 3.28, total_amount: 36.1 },
      ],
      money: ["tax_amount", "total_amount"],
    },
  },
  "delivery-note-to-excel": {
    ns: "deliveryNoteTool",
    template: "deliverynote",
    sample: {
      file: "delivery-note-sample.pdf",
      sheet: "DN-2026-1187.xlsx",
      columns: ["reference", "description", "quantity", "unit"],
      rows: [
        { reference: "AO-2210", description: "Copy paper A4 80 g/m², box of 5 reams", quantity: 20, unit: "box" },
        { reference: "AO-5031", description: "Lever arch file, 75 mm spine, grey", quantity: 60, unit: "pcs" },
        { reference: "AO-7788", description: "Ergonomic office chair, black mesh", quantity: 4, unit: "pcs" },
        { reference: "AO-1102", description: "Toner cartridge, black, high yield", quantity: 8, unit: "pcs" },
        { reference: "AO-3340", description: "Desk organiser set, recycled plastic", quantity: 12, unit: "set" },
      ],
      money: [],
    },
  },
  "quote-to-excel": {
    ns: "quoteTool",
    template: "quote",
    sample: {
      file: "quote-sample.pdf",
      sheet: "quotes.xlsx",
      columns: ["quote_number", "issue_date", "valid_until", "supplier_name", "subtotal", "tax_amount", "total_amount"],
      rows: [
        { quote_number: "Q-2026-0342", issue_date: "2026-08-28", valid_until: "2026-09-27", supplier_name: "Lemanic Renovations SA", subtotal: 12930, tax_amount: 1047.33, total_amount: 13977.33 },
      ],
      money: ["subtotal", "tax_amount", "total_amount"],
    },
  },
  "purchase-order-to-excel": {
    ns: "purchaseOrderTool",
    template: "purchaseorder",
    sample: {
      file: "purchase-order-sample.pdf",
      sheet: "PO-GH-2026-0218.xlsx",
      columns: ["reference", "description", "quantity", "unit_price", "amount"],
      rows: [
        { reference: "OL-KS-400", description: "King size bed sheet set, 400 thread count", quantity: 120, unit_price: 185, amount: 22200 },
        { reference: "OL-BT-700", description: "Bath towel, 700 gsm, white", quantity: 300, unit_price: 38.5, amount: 11550 },
        { reference: "OL-PW-050", description: "Pillow, hollow fibre, 50×75 cm", quantity: 200, unit_price: 42, amount: 8400 },
      ],
      money: ["unit_price", "amount"],
    },
  },
  // Extractos bancarios (24-9-2026). Salida real de la muestra, consultada en
  // scanner_results: 22 movimientos y el saldo cuadra (balance_check.ok).
  "bank-statement-to-excel": {
    ns: "bankStatementTool",
    template: "bankstatement",
    sample: {
      file: "bank-statement-sample.pdf",
      sheet: "statement-august-2026.xlsx",
      columns: ["date", "description", "amount", "balance"],
      rows: [
        { date: "2026-08-01", description: "CARD PAYMENT TO CAFE EXAMPLE REF 291461", amount: -404.39, balance: 3806.16 },
        { date: "2026-08-02", description: "STANDING ORDER OFFICE RENT REF 303523", amount: -479.19, balance: 3326.97 },
        { date: "2026-08-03", description: "FASTER PAYMENT FROM DEMO CLIENT LTD REF 196245", amount: 1659.5, balance: 4986.47 },
        { date: "2026-08-04", description: "FASTER PAYMENT FROM DEMO CLIENT LTD REF 397136", amount: 1964.36, balance: 6950.83 },
        { date: "2026-08-06", description: "CARD PAYMENT TO SAMPLE GROCERS REF 856053", amount: -216.33, balance: 6734.5 },
      ],
      money: ["amount", "balance"],
    },
  },
  // Varias tarjetas de visita en una hoja (25-9-2026). Hueco del radar: tras
  // una feria la gente escanea montones de tarjetas y las teclea a mano; en
  // Google salen blogs y plantillas, casi ninguna herramienta. La plantilla
  // businesscard es una tabla de contactos (una fila por tarjeta). Salida real
  // de public/samples/business-cards-sheet.jpg con gemini-3.8-flash: 8/8.
  "business-cards-to-excel": {
    ns: "businessCardsTool",
    template: "businesscard",
    widgetSample: { path: "/samples/business-cards-sheet.jpg", name: "business-cards-sheet.jpg", type: "image/jpeg", labelKey: "businessCardsTool.sampleButton" },
    sample: {
      file: "business-cards-sheet.jpg",
      sheet: "trade-show-contacts.xlsx",
      columns: ["full_name", "job_title", "company_name", "phone", "email"],
      rows: [
        { full_name: "James Whitfield", job_title: "Head of Procurement", company_name: "Harbourline Energy Ltd", phone: "+44 20 7946 0831", email: "j.whitfield@harbourline.example" },
        { full_name: "Dr. Katrin Vogel", job_title: "Geschäftsführerin", company_name: "Solarwerk Nord GmbH", phone: "+49 40 5550 1924", email: "k.vogel@solarwerk-nord.example" },
        { full_name: "Marta Llorens Pérez", job_title: "Directora Comercial", company_name: "Rivera & Llorens Iluminación S.L.", phone: "655 21 09 87 / 910 55 44 33", email: "marta@riverallorens.example" },
        { full_name: "Giulia Moretti", job_title: "Responsabile Acquisti", company_name: "Verdeluce S.r.l.", phone: "+39 02 5550 7712", email: "giulia.moretti@verdeluce.example" },
        { full_name: "Arjun Mehta", job_title: "Business Development Manager", company_name: "Ocean Grid Technologies Pvt Ltd", phone: "+91 98765 43210", email: "arjun.mehta@oceangrid.example" },
        { full_name: "Sophie Laurent", job_title: "Chargée d'affaires", company_name: "Lumen Habitat SAS", phone: "+33 1 55 50 12 34", email: "s.laurent@lumenhabitat.example" },
        { full_name: "Rui Tavares", job_title: "Diretor Técnico", company_name: "Tavares & Filhos, Lda.", phone: "+351 21 555 0198", email: "rui.tavares@tavaresfilhos.example" },
        { full_name: "Omar Al-Hashimi", job_title: "Procurement Director", company_name: "Desert Bloom Facilities LLC", phone: "+971 4 555 0142", email: "omar@desertbloom.example" },
      ],
      money: [],
    },
  },
  // PDF escaneado / tabla cualquiera a Excel (25-9-2026), con el modo
  // automático. Lo más repetido en r/excel: «los conversores me descolocan las
  // columnas» y «Excel me convierte los códigos en fechas». Medido con 5 tablas
  // (zsh_scripts/free_demo/tablas/): 99,3 % de celdas. Salida real de
  // public/samples/scanned-table-sample.jpg (12/12 filas; «1L» es el OCR).
  "scanned-pdf-to-excel": {
    ns: "scannedPdfTool",
    template: "auto",
    widgetSample: { path: "/samples/scanned-table-sample.jpg", name: "scanned-table-sample.jpg", type: "image/jpeg", labelKey: "scannedPdfTool.sampleButton" },
    sample: {
      file: "scanned-table-sample.jpg",
      sheet: "chemical-inventory.xlsx",
      columns: ["substance", "cas_number", "container", "shelf", "lot"],
      rows: [
        { substance: "Vinyl chloride", cas_number: "75-01-4", container: "1 kg", shelf: "A-02", lot: "12-2" },
        { substance: "Water", cas_number: "7732-18-5", container: "1 kg", shelf: "A-02", lot: "2-27" },
        { substance: "Ethanol", cas_number: "64-17-5", container: "1 kg", shelf: "A-02", lot: "7-28" },
        { substance: "Acetone", cas_number: "67-64-1", container: "1 kg", shelf: "C-03", lot: "7-26" },
        { substance: "Toluene", cas_number: "108-88-3", container: "25 kg", shelf: "C-03", lot: "8-2" },
        { substance: "Benzene", cas_number: "71-43-2", container: "25 kg", shelf: "B-11", lot: "3-2" },
      ],
      money: [],
    },
  },
};

export const DOC_TOOL_SLUGS = Object.keys(DOC_TOOLS);
